import { ResponsiveStylesStructured } from '@duda-co/responsive-styles/types/ResponsiveStylesTypes';

/**
 * A site-theme text style. `default` is the theme's default text style (DFLT in
 * the editor dropdown), `p` its paragraph style (PAR) and `h1`-`h6` its heading
 * styles. Anything other than `default` renders the matching element so the
 * site theme's own rules style it.
 */
export type OrderConfirmationTextStyle =
    | 'default'
    | 'p'
    | 'h1'
    | 'h2'
    | 'h3'
    | 'h4'
    | 'h5'
    | 'h6';

export type OrderConfirmationTextRole =
    | 'primaryTitle'
    | 'detailsTitle'
    | 'primaryText'
    | 'secondaryText';

export type OrderConfirmationTextStyles = Partial<
    Record<OrderConfirmationTextRole, OrderConfirmationTextStyle>
>;

/** Style keys the editor persists under the widget's `_styles` model. */
export type OrderConfirmationStyleKey = OrderConfirmationTextRole | 'dividers';

export type OrderConfirmationStyles = Partial<
    Record<OrderConfirmationStyleKey, ResponsiveStylesStructured>
>;

/**
 * `data-grab` values the editor's design panel targets. Text roles sit on the
 * real text elements (so color changes preview live in the canvas) and
 * `dividers` on every divider and bordered box.
 */
export const DATA_GRABS: Record<OrderConfirmationStyleKey, string> = {
    primaryTitle: 'order-confirmation-primary-title',
    detailsTitle: 'order-confirmation-details-title',
    primaryText: 'order-confirmation-primary-text',
    secondaryText: 'order-confirmation-secondary-text',
    dividers: 'order-confirmation-divider',
};

/** Mirrors the tags the widget rendered before text styles were selectable. */
export const DEFAULT_TEXT_STYLES: Required<OrderConfirmationTextStyles> = {
    primaryTitle: 'h2',
    detailsTitle: 'h3',
    primaryText: 'default',
    secondaryText: 'default',
};

/**
 * "Platform/Selectable Item" in Figma, which `@duda-co/styles` names
 * `$selectable-item-color`. Every color option starts here.
 *
 * Held as a literal on purpose: `cssVariables.iconColor` happens to carry the
 * same value today, but it is a shared cross-widget token for icon fills, so
 * retuning it must not move this widget's text and border defaults.
 */
export const SELECTABLE_ITEM_COLOR = '#616C79';

export const DEFAULT_COLORS: Record<OrderConfirmationStyleKey, string> = {
    primaryTitle: SELECTABLE_ITEM_COLOR,
    detailsTitle: SELECTABLE_ITEM_COLOR,
    primaryText: SELECTABLE_ITEM_COLOR,
    secondaryText: SELECTABLE_ITEM_COLOR,
    dividers: SELECTABLE_ITEM_COLOR,
};

export interface OrderConfirmationDesign {
    textStyles: Required<OrderConfirmationTextStyles>;
    /** Always resolved: a picked color, or this role's default. */
    colors: Record<OrderConfirmationStyleKey, string>;
}

export const DEFAULT_DESIGN: OrderConfirmationDesign = {
    textStyles: DEFAULT_TEXT_STYLES,
    colors: DEFAULT_COLORS,
};

// The editor writes kebab-case CSS properties into `common`, so these are read
// off the raw record rather than through the camel-cased CSSProperties type.
function readColor(
    styles: ResponsiveStylesStructured | undefined,
    property: 'color' | 'border-color'
): string | undefined {
    const common = styles?.common as Record<string, string> | undefined;
    return common?.[property] || undefined;
}

export function resolveDividersColor(
    styles: OrderConfirmationStyles | undefined
): string {
    return (
        readColor(styles?.dividers, 'border-color') ?? DEFAULT_COLORS.dividers
    );
}

export function resolveDesign(
    textStyles: OrderConfirmationTextStyles | undefined,
    styles: OrderConfirmationStyles | undefined
): OrderConfirmationDesign {
    return {
        textStyles: { ...DEFAULT_TEXT_STYLES, ...textStyles },
        colors: {
            primaryTitle:
                readColor(styles?.primaryTitle, 'color') ??
                DEFAULT_COLORS.primaryTitle,
            detailsTitle:
                readColor(styles?.detailsTitle, 'color') ??
                DEFAULT_COLORS.detailsTitle,
            primaryText:
                readColor(styles?.primaryText, 'color') ??
                DEFAULT_COLORS.primaryText,
            secondaryText:
                readColor(styles?.secondaryText, 'color') ??
                DEFAULT_COLORS.secondaryText,
            dividers: resolveDividersColor(styles),
        },
    };
}
